import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  CONTACT_UPDATE_STEP,
  selectContact,
  selectContactChange,
  selectContactUpdateStep,
  selectCreateNewCRMContact,
  selectIsInitializing,
  selectNewContactData,
  selectSelectedKeys,
  selectSelectedZocksContact,
  selectShouldCreateContact,
  selectShouldLinkContact,
  selectUpdateStartedFrom,
  setContact,
  setContactChange,
  setContactChanges,
  setCreateNewCRMContact,
  setIsInitializing,
  setIsLoadingContact,
  setIsLoadingContactChange,
  setIsLoadingRerun,
  setIsLoadingSave,
  setIsSearchingForEmail,
  setSelectedKeys,
  setShouldCreateContact,
  setShouldReload,
  setSegmentLoadingState,
} from '../../store/meetingSummary/contactUpdate'

import {
  useCreateContactChangeMutation,
  useDeleteContactChangeMutation,
  useLazyGetAllContactChangesQuery,
  useLazyGetContactChangesQuery,
  useUpdateContactChangeMutation,
} from '../../store/meetingSupport/meetingSupport'
import {
  useCreateIntegrationContactMutation,
  useGetContactSchemaQuery,
  useLazyGetIntegrationUsersQuery,
  useUpdateIntegrationUserMutation,
} from '../../store/integrations/integrations'
import { useSaveExternalUserByIdMutation } from '../../store/externalUser/externalUser'
import { usePromoteUserByIdMutation } from '../../store/user/user'
import { AIAgentAttachment, documentFileToAttachment, useExecuteAiAgentMutation } from '../../store/ai/aiAgent'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { addToast } from '../../store/toast/toastSlice'
import { AIAgentType } from '../../models/AIAgent'

import {
  getIntegrationDisplayName,
  INTEGRATIONS_WITH_DYNAMIC_SCHEMA,
  INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE,
  IntegrationServiceId,
} from '../../common/integrations'
import { ExternalUserServiceIntegration, SERVICE_ID_TO_INTEGRATION_TYPE_MAPPING, User, UserOwnerType } from '../../models/User'
import {
  ChildStatus,
  filterDataByKeys,
  getChildStatus,
  getFailedStepsInChild,
  getFieldErrorMessages,
  getFieldSubSectionId,
  getNumberOfChildren,
  getPanelRequiredSteps,
  getShouldDisplayStatusIcon,
  omitReadOnlyValues,
  OWNER_PATHS_BY_FIELD,
  PanelStatus,
  processContactUpdateAgentResult,
  removeEmptyFields,
  stripReadOnlyProperties,
  IGNORE_FIELDS,
  DEFAULT_IGNORE_FIELDS,
  SEPARATOR,
} from './utils'

import { Account, CRMAccountIntegrationConfig } from '../../models/Account'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { DEFAULT_CONTACT_ROLE_ID } from '../Contacts/AddContactModal'
import { selectClientProfileScorecards } from '../../store/meetingSummary/clientProfile'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { ContactUpdateFormValues } from './models'
import { NavigationSidebarSection } from '../../components/NavigationWithSideBar/NavigationSidebar'
import { SetValueConfig } from 'react-hook-form'
import { DocumentFile } from '../../models/Document'

const getSchemaBasedKeys = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any,
  currentPath: string,
  serviceId: string,
  isRequired: boolean = false,
): string[] => {
  if (!schema) {
    return []
  }

  const pathParts = currentPath.split(SEPARATOR)
  const fieldName = pathParts[pathParts.length - 1]
  const ignoreList = IGNORE_FIELDS[serviceId] ?? DEFAULT_IGNORE_FIELDS

  if (ignoreList.includes(fieldName)) {
    return []
  }

  if (schema.const !== undefined) {
    return []
  }

  const isContainer = schema.type === 'object' || (schema.type === 'array' && schema.items)

  if (!isContainer) {
    const value = data
    const isEmpty =
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'object' && Object.keys(value).length === 0)

    if (!isRequired && isEmpty) {
      return []
    }
    return [currentPath]
  }

  if (schema.type === 'array' && schema.items) {
    if (!Array.isArray(data) || data.length === 0) {
      return []
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return data.flatMap((item: any, index: number) => getSchemaBasedKeys(schema.items, item, `${currentPath}.${index}`, serviceId, false))
  }

  if (schema.type === 'object' && schema.properties) {
    const requiredFields = Array.isArray(schema.required) ? schema.required : []

    return Object.keys(schema.properties).flatMap((key) => {
      const nextPath = `${currentPath}.${key}`
      const isFieldRequired = requiredFields.includes(key)
      return getSchemaBasedKeys(schema.properties[key], data?.[key], nextPath, serviceId, isFieldRequired)
    })
  }

  return [currentPath]
}

export const useSelectContactFormData = (
  serviceId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any,
  keyParts: string[],
  onKeySelected?: (selectedKey: string, isUnselect: boolean) => void,
) => {
  const dispatch = useAppDispatch()

  const selectedKeys = useAppSelector(selectSelectedKeys)

  const validTargetKeys = useMemo(() => {
    return getSchemaBasedKeys(schema, data, keyParts.join('.'), serviceId, false)
  }, [schema, data, keyParts, serviceId])

  const isSelected = useMemo(() => {
    if (validTargetKeys.length === 0) {
      return false
    }
    return validTargetKeys.every((e) => selectedKeys.includes(e))
  }, [validTargetKeys, selectedKeys])

  const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const newSet = new Set([...selectedKeys, ...validTargetKeys])
      dispatch(setSelectedKeys(Array.from(newSet)))
    } else {
      const newKeys = selectedKeys.filter((e) => !validTargetKeys.includes(e))
      dispatch(setSelectedKeys(newKeys))
    }
    if (onKeySelected) {
      onKeySelected(keyParts[keyParts.length - 1], !e.target.checked)
    }
  }

  return { isSelected, handleSelect }
}

export interface ExternalEmailContactUpdateProps {
  type: 'email'
  emailConnector: string
  threadId: string
  scorecardIds?: string[] | null
}

export interface ExternalMeetingContactUpdateProps {
  type: 'meeting'
  roomId: string
  sessionId: string
  meetingTemplateId: string
  scorecardIds?: string[] | null
}

export interface ExternalDocumentContactUpdateProps {
  type: 'document'
  documentId: string
  files: DocumentFile[]
  scorecardIds?: string[] | null
}

export type ExternalContactUpdateProps = ExternalEmailContactUpdateProps | ExternalMeetingContactUpdateProps | ExternalDocumentContactUpdateProps

const getFilters = (updateProps: ExternalContactUpdateProps) => {
  const { type } = updateProps

  if (type === 'email') {
    const { emailConnector, threadId } = updateProps
    return { type, typeKey: `${emailConnector}|${threadId}` }
  }

  if (type === 'meeting') {
    const { roomId, sessionId } = updateProps
    return { type, typeKey: `${roomId}|${sessionId}` }
  }

  const { documentId } = updateProps
  return { type, typeKey: documentId }
}

const getTypeParams = (updateProps: ExternalContactUpdateProps) => {
  const { type } = updateProps

  if (type === 'email') {
    const { emailConnector, threadId } = updateProps
    return { type, emailConnector, threadId }
  }

  if (type === 'meeting') {
    const { roomId, sessionId, meetingTemplateId } = updateProps
    return { type, roomId, sessionId, meetingTemplateId }
  }

  const { documentId } = updateProps
  return { type, documentId }
}

interface ContactUpdateRunContext {
  aiAgentType?: AIAgentType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  aiAgentExecutionContext?: Record<string, any>
  aiAgentAttachments?: AIAgentAttachment[]
}

const getContactUpdateRunContext = (
  user: User,
  updateProps: ExternalContactUpdateProps,
  serviceId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactValues: Record<string, any> | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactSchema: Record<string, any>,
  externalId: string,
): ContactUpdateRunContext => {
  const { type } = updateProps
  const { id: contactId, name: contactName, email: contactEmail } = user
  const { scorecardIds } = updateProps

  if (type === 'email') {
    const { emailConnector, threadId } = updateProps
    const emailAIAgentExecutionContext = {
      contactName,
      contactEmail,
      emailConnector,
      threadId,
      serviceId,
      values: contactValues,
      schema: contactSchema,
      ...(scorecardIds && scorecardIds.length > 0 && { scorecardIds }),
    }
    return {
      aiAgentType: !externalId || externalId === 'new' ? AIAgentType.EMAIL_CONTACT_CREATE : AIAgentType.EMAIL_CONTACT_UPDATE,
      aiAgentExecutionContext: emailAIAgentExecutionContext,
    }
  }

  if (type === 'meeting') {
    const { roomId, sessionId, meetingTemplateId } = updateProps
    const meetingAIAgentExecutionContext = {
      contactId,
      meetingTemplateId,
      roomId,
      sessionId,
      serviceId,
      values: contactValues,
      schema: contactSchema,
      ...(scorecardIds && scorecardIds.length > 0 && { scorecardIds }),
    }
    return {
      aiAgentType: !externalId || externalId === 'new' ? AIAgentType.CONTACT_CREATE : AIAgentType.CONTACT_UPDATE,
      aiAgentExecutionContext: meetingAIAgentExecutionContext,
    }
  }

  const { documentId, files } = updateProps

  const aiAgentAttachments: AIAgentAttachment[] = files.map(documentFileToAttachment)

  const documentAIAgentExecutionContext = {
    contactId,
    documentId,
    serviceId,
    previousValues: contactValues,
    schema: contactSchema,
    ...(scorecardIds && scorecardIds.length > 0 && { scorecardIds }),
  }
  return {
    aiAgentType: AIAgentType.DOCUMENT_CONTACT_UPDATE,
    aiAgentExecutionContext: documentAIAgentExecutionContext,
    aiAgentAttachments,
  }
}

export const useExternalContactUpdate = (serviceId: string, user: User, updateProps: ExternalContactUpdateProps) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const [triggerGetAllContactChanges] = useLazyGetAllContactChangesQuery()
  const [triggerCreateContactChange] = useCreateContactChangeMutation()
  const [triggerUpdateContactChange] = useUpdateContactChangeMutation()

  const fetchContactChange = useCallback(
    async (externalId: string) => {
      try {
        const filters = getFilters(updateProps)
        const { data: contactChanges } = await triggerGetAllContactChanges({
          accountId,
          filters,
        }).unwrap()

        dispatch(setContactChanges(contactChanges))

        return contactChanges.find((e) => e.serviceId === serviceId && e.externalId === externalId) ?? null
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, serviceId, triggerGetAllContactChanges, updateProps, dispatch],
  )

  const fetchAllContactChanges = useCallback(async () => {
    try {
      const filters = getFilters(updateProps)
      const { data: contactChanges } = await triggerGetAllContactChanges(
        {
          accountId,
          filters,
        },
        false,
      ).unwrap()

      dispatch(setContactChanges(contactChanges))
      return contactChanges
    } catch (e) {
      console.error(e)
      return []
    }
  }, [accountId, dispatch, triggerGetAllContactChanges, updateProps])

  const createContactChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (externalId: string, contactSchema: Record<string, any>, previousContactValues: Record<string, any>, contactValues: Record<string, any>) => {
      try {
        const typeParams = getTypeParams(updateProps)
        const payload = {
          ...typeParams,
          userId: user.id,
          serviceId,
          externalId,
          previousContactValues,
          contactValues,
          contactSchema,
        }
        return triggerCreateContactChange({ accountId, payload }).unwrap()
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, serviceId, triggerCreateContactChange, updateProps, user],
  )

  const updateContactChangeById = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (contactChangeId: string, contactValues: Record<string, any>) => {
      try {
        return await triggerUpdateContactChange({
          accountId,
          contactChangeId,
          payload: { contactValues },
        }).unwrap()
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerUpdateContactChange],
  )

  const executeContactUpdateAgent = useCallback(
    async (aiAgentType: AIAgentType, aiAgentExecutionContext: ContactUpdateRunContext, aiAgentAttachments?: AIAgentAttachment[]) => {
      try {
        return await triggerExecuteAiAgent({
          accountId,
          payload: {
            type: aiAgentType,
            context: aiAgentExecutionContext,
            attachments: aiAgentAttachments,
          },
        }).unwrap()
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerExecuteAiAgent],
  )

  const generateContactValues = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (contactValues: Record<string, any> | null, contactSchema: Record<string, any>, externalId: string) => {
      const { aiAgentType, aiAgentExecutionContext, aiAgentAttachments } = getContactUpdateRunContext(
        user,
        updateProps,
        serviceId,
        contactValues,
        stripReadOnlyProperties(contactSchema),
        externalId,
      )
      if (aiAgentType && aiAgentExecutionContext) {
        const aiAgentResult = await executeContactUpdateAgent(aiAgentType, aiAgentExecutionContext, aiAgentAttachments)
        if (!aiAgentResult) {
          return null
        }
        return processContactUpdateAgentResult(serviceId, contactSchema, contactValues, aiAgentResult)
      }
      return null
    },
    [executeContactUpdateAgent, serviceId, updateProps, user],
  )

  return {
    fetchContactChange,
    createContactChange,
    updateContactChangeById,
    generateContactValues,
    fetchAllContactChanges,
  }
}

export const useGetContactChange = (serviceId: string, user: User, updateProps: ExternalContactUpdateProps) => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()
  const scorecards = useAppSelector(selectClientProfileScorecards)

  const scorecardIds = useMemo(() => {
    const filteredScorecards = scorecards.filter((scorecard) => scorecard.config.includeInCreateClientProfile)
    if (!filteredScorecards.length) {
      return null
    }
    return filteredScorecards.map((scorecard) => scorecard.id)
  }, [scorecards])

  const enhancedUpdateProps: ExternalContactUpdateProps = useMemo(
    () => ({
      ...updateProps,
      scorecardIds: scorecardIds,
    }),
    [updateProps, scorecardIds],
  )

  const { createContactChange, updateContactChangeById, generateContactValues, fetchAllContactChanges } = useExternalContactUpdate(
    serviceId,
    user,
    enhancedUpdateProps,
  )

  const generateDefaultContactChange = useCallback(
    async (
      externalId: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      contactSchema: { [key: string]: any },
      previousContactValues: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any
      } | null,
    ) => {
      const keys = Object.keys(contactSchema['properties'])

      const _previousContactValues = !previousContactValues ? Object.fromEntries(keys.map((key) => [key, null])) : previousContactValues
      const contactValues = await generateContactValues(_previousContactValues, contactSchema, externalId)
      if (!contactValues) {
        return null
      }
      return createContactChange(externalId, contactSchema, _previousContactValues, contactValues)
    },
    [createContactChange, generateContactValues],
  )

  const generateSegmentedContactChange = useCallback(
    async (
      externalId: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      contactSchema: { [key: string]: any },
      previousContactValues: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any
      } | null,
    ) => {
      const keys = Object.keys(contactSchema)

      const _contactSchema = Object.fromEntries(keys.map((key) => [key, contactSchema[key]['schema']]))
      const _previousContactValues = Object.fromEntries(keys.map((key) => [key, previousContactValues ? previousContactValues[key]['values'] : null]))

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const accumulatedContactValues: Record<string, any> = {}
      keys.forEach((key) => {
        accumulatedContactValues[key] = null
      })

      const activeContactChange = {
        current: await createContactChange(externalId, _contactSchema, _previousContactValues, { ...accumulatedContactValues }),
      }

      if (!activeContactChange.current) {
        return null
      }
      dispatch(setContactChange(activeContactChange.current))

      const _generateContactValues = async (key: string) => {
        dispatch(setSegmentLoadingState({ segmentKey: key, isLoading: true }))
        try {
          const result = await generateContactValues(
            previousContactValues ? previousContactValues[key]['values'] : null,
            contactSchema[key]['schema'],
            externalId,
          )

          accumulatedContactValues[key] = result

          if (activeContactChange.current?.id) {
            const updatedChange = await updateContactChangeById(activeContactChange.current.id, { ...accumulatedContactValues })
            if (updatedChange) {
              activeContactChange.current = updatedChange
              dispatch(setContactChange(updatedChange))
            }
          }

          return result
        } finally {
          dispatch(setSegmentLoadingState({ segmentKey: key, isLoading: false }))
          dispatch(setIsLoadingContactChange(false))
        }
      }

      await Promise.all(keys.map((key) => _generateContactValues(key)))

      return activeContactChange.current
    },
    [createContactChange, dispatch, generateContactValues, updateContactChangeById],
  )

  const _generateContactChange = useCallback(
    async (
      externalId: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      contactSchema: { [key: string]: any },
      previousContactValues: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any
      } | null,
    ) => {
      if (INTEGRATIONS_WITH_SEGMENTED_CONTACT_CHANGE.includes(serviceId as IntegrationServiceId)) {
        return generateSegmentedContactChange(externalId, contactSchema, previousContactValues)
      }
      return generateDefaultContactChange(externalId, contactSchema, previousContactValues)
    },
    [generateDefaultContactChange, generateSegmentedContactChange, serviceId],
  )

  const shouldCreateContact = useAppSelector(selectCreateNewCRMContact)

  const generateContactChange = useMemo(
    () =>
      tracked(_generateContactChange, {
        eventName: TrackEvent.CONTACT_UPDATE_RESULTS_CREATED,
        eventDetails: {
          type: shouldCreateContact ? 'create' : 'update',
          serviceId: serviceId,
        },
      }),
    [_generateContactChange, shouldCreateContact, tracked, serviceId],
  )

  const getContactChange = useCallback(
    async (
      externalId: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      contactSchema: { [key: string]: any },
      previousContactValues: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any
      } | null,
    ) => {
      dispatch(setContactChange(null))
      dispatch(setIsLoadingContactChange(true))
      try {
        const changes = await fetchAllContactChanges()
        const contactChange = externalId ? (changes.find((e) => e.serviceId === serviceId && e.externalId === externalId) ?? null) : null

        if (contactChange) {
          dispatch(setContactChange(contactChange))
          return
        }
        const generatedContactChange = await generateContactChange(externalId, contactSchema, previousContactValues)
        if (!generatedContactChange) {
          return
        }
        dispatch(setContactChange(generatedContactChange))
      } catch (e) {
        console.log(e)
      } finally {
        dispatch(setIsLoadingContactChange(false))
      }
    },
    [dispatch, fetchAllContactChanges, generateContactChange, serviceId],
  )

  return { getContactChange }
}

interface UseSaveContactUpdateProps {
  serviceId: string
  contactId: string
  user: User
  updateProps: ExternalContactUpdateProps
  onSave?: () => void
}

export const useSaveContactUpdate = ({ serviceId, contactId, user, updateProps, onSave }: UseSaveContactUpdateProps) => {
  const dispatch = useAppDispatch()

  const { trackingClient } = useTracking()
  const updateStartedFrom = useAppSelector(selectUpdateStartedFrom)

  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const newContactData = useAppSelector(selectNewContactData)

  const contact = useAppSelector(selectContact)
  const createNewCRMContact = useAppSelector(selectCreateNewCRMContact)
  const contactChange = useAppSelector(selectContactChange)
  const shouldLinkContact = useAppSelector(selectShouldLinkContact)
  const shouldCreateContact = useAppSelector(selectShouldCreateContact)
  const selectedKeys = useAppSelector(selectSelectedKeys)
  const selectedZocksContact = useAppSelector(selectSelectedZocksContact)
  const [triggerUpdateIntegrationUser] = useUpdateIntegrationUserMutation()
  const [triggerCreateIntegrationContact] = useCreateIntegrationContactMutation()
  const [triggerUpdateExternalUser, { error: updateExternalUserError }] = useSaveExternalUserByIdMutation()
  const [triggerPromoteUser, { error: promoteUserError }] = usePromoteUserByIdMutation()
  const [triggerGetContactChanges] = useLazyGetContactChangesQuery()

  const refetchContactChanges = async (externalId?: string | null) => {
    const filters = getFilters(updateProps)

    try {
      const contactChanges = await triggerGetContactChanges(
        {
          accountId,
          filters,
          pageRequest: { page: 0, size: 500 },
        },
        false,
      ).unwrap()

      dispatch(setContactChanges(contactChanges))

      if (externalId) {
        const change = contactChanges.find((contactChange) => contactChange.serviceId === serviceId && contactChange.externalId === externalId)
        if (change) {
          dispatch(setContactChange(change))
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  const saveContactUpdate = async ({ contactValues, metadata }: ContactUpdateFormValues) => {
    try {
      dispatch(setIsLoadingSave(true))
      if (!contactChange) {
        return
      }

      const result = await _saveContactUpdate(contactValues, metadata)

      const integrationDisplayName = getIntegrationDisplayName(serviceId as IntegrationServiceId)
      if (!result) {
        return
      }

      if (result && result.data && result.data.data && !result.error) {
        const externalContact = result.data.data
        const externalId =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          externalContact.id || (externalContact as any).externalId || externalContact.integrations?.find((i) => i.serviceId === serviceId)?.externalId

        if (shouldCreateContact) {
          await triggerPromoteUser({
            accountId,
            userId: contactId,
            userData: {
              firstName: externalContact.firstName,
              lastName: externalContact.lastName,
              name: externalContact.name,
              email: contact?.email ?? externalContact.email,
              roleIds: [DEFAULT_CONTACT_ROLE_ID],
              integrations: externalContact.integrations,
              identity: {
                type: 'external',
                location: contact?.email ?? externalContact.email,
              },
              owner: {
                ownerId: currentUser.id,
                ownerType: UserOwnerType.USER,
              },
            },
          })
          if (externalId) {
            await refetchContactChanges(externalId)
          }
        }

        if (shouldLinkContact) {
          const mergedIntegration = (selectedZocksContact?.integrations ?? [])
            .filter((i) => i.serviceId !== serviceId)
            .map((i) => {
              return {
                ...i,
                type: i.type || SERVICE_ID_TO_INTEGRATION_TYPE_MAPPING[i.serviceId as IntegrationServiceId] || 'crm',
              }
            })
            .concat(externalContact.integrations ?? [])

          await triggerUpdateExternalUser({
            accountId,
            userId: contactId,
            userData: {
              referenceUserId: null,
              integrations: mergedIntegration,
              owner: {
                ownerId: currentUser.id,
                ownerType: UserOwnerType.USER,
              },
            },
          })
          if (externalId) {
            await refetchContactChanges(externalId)
          }
        }

        if (!shouldCreateContact && !shouldLinkContact && externalId) {
          await refetchContactChanges(externalId)
        }

        if (promoteUserError || updateExternalUserError) {
          const saveError = promoteUserError ?? updateExternalUserError
          dispatch(
            addToast({
              variant: 'error',
              title: 'Contact save to Zocks failed',
              messages: [
                typeof saveError === 'object' && saveError != null && 'message' in saveError && saveError.message
                  ? saveError.message
                  : 'Something went wrong. If the issue persists, please contact us at support@zocks.io',
              ],
            }),
          )
        } else {
          dispatch(
            addToast({
              variant: 'success',
              title: `Contact ${shouldCreateContact ? 'created' : 'updated'} in Zocks`,
              messages: [`Contact ${shouldCreateContact ? 'created' : 'updated'} in Zocks`],
            }),
          )
        }
        if (result.data.errors) {
          dispatch(
            addToast({
              variant: 'warning',
              title: `Contact partially ${createNewCRMContact ? 'created' : 'updated'}`,
              messages: [`Your ${integrationDisplayName} contact was partially ${createNewCRMContact ? 'created' : 'updated'}.`],
            }),
          )
          onSave?.()
        } else {
          dispatch(
            addToast({
              variant: 'success',
              title: `Contact ${createNewCRMContact ? 'created' : 'updated'}`,
              messages: [`Your ${integrationDisplayName} contact was successfully ${createNewCRMContact ? 'created' : 'updated'}`],
            }),
          )
          onSave?.()
        }
      }
    } catch {
      dispatch(
        addToast({
          variant: 'error',
          title: `Failed to ${createNewCRMContact ? 'create' : 'update'} Contact`,
          messages: ['Something went wrong. If the issue persists, please contact us at support@zocks.io'],
        }),
      )
    } finally {
      dispatch(setIsLoadingSave(false))
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _saveContactUpdate = async (contactValues: Record<string, any>, metadata?: Record<string, unknown>) => {
    if (createNewCRMContact && !INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId as IntegrationServiceId)) {
      trackingClient.track({
        eventName: TrackEvent.EXTERNAL_CONTACT_CREATED,
        eventDetails: {
          serviceId,
          ZocksContactType: user?.type,
          source: updateStartedFrom,
        },
      })

      return _createContact(contactValues)
    }
    return _updateContact(contactValues, metadata)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _createContact = async (contactValues: Record<string, any>) => {
    if (!newContactData || !contactChange) {
      return
    }
    const filteredData = filterDataByKeys({ contactValues }, selectedKeys)
    const writableData = { contactValues: omitReadOnlyValues(contactChange.contactSchema, filteredData.contactValues) }
    const filteredContactValues = removeEmptyFields(serviceId, writableData)
    const { firstName, lastName, name, email, metadata, ownerId } = newContactData
    return await triggerCreateIntegrationContact({
      accountId,
      connector: serviceId,
      contact: filteredContactValues.contactValues,
      firstName,
      lastName,
      name,
      email,
      metadata,
      contactChangeId: contactChange.id,
      internalContactId: user.id,
      ownerId,
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const _updateContact = async (contactValues: Record<string, any>, metadata?: Record<string, unknown>) => {
    try {
      if (!currentUser || !contact || !contactChange) {
        return
      }
      const integration = contact.integrations?.find((e) => e.serviceId === serviceId)
      if (!integration) {
        return
      }
      const filteredData = filterDataByKeys({ contactValues }, selectedKeys)
      const writableData = { contactValues: omitReadOnlyValues(contactChange.contactSchema, filteredData.contactValues) }
      const filteredContactValues = removeEmptyFields(serviceId, writableData)

      return await triggerUpdateIntegrationUser({
        accountId,
        connector: serviceId,
        relatedTo: {
          id: integration.externalId,
        },
        payload: filteredContactValues.contactValues ?? {},
        contactChangeId: contactChange.id,
        metadata,
      })
    } catch (e) {
      console.error(e)
    }
  }

  return { saveContactUpdate }
}

const useGetContactSchemaArgs = (account: Account | null, user: User | null, serviceId: string) => {
  const accountId = useAppSelector(selectAccountId)
  return !account
    ? skipToken
    : {
        accountId,
        connector: serviceId,
        context: {
          objectName: (() => {
            const integration = account?.config.integrations.find((i) => i.serviceId === serviceId && i.type === 'crm') as
              | CRMAccountIntegrationConfig
              | undefined

            const contactMapping = integration?.mappings?.contact

            if (contactMapping && typeof contactMapping === 'object' && 'objectName' in contactMapping) {
              return contactMapping.objectName
            }

            return 'contact'
          })(),
          relatedTo: (() => {
            if (!user) {
              return
            }
            const integration = user?.integrations?.find((i) => i.serviceId === serviceId) as ExternalUserServiceIntegration | undefined
            if (!integration) {
              return
            }
            return {
              id: integration.externalId,
              type: integration?.type,
            }
          })(),
        },
      }
}

export const useRerunContactUpdate = (serviceId: string, user: User, updateProps: ExternalContactUpdateProps, onRerun?: () => void) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const contact = useAppSelector(selectContact)
  const contactChange = useAppSelector(selectContactChange)

  const [triggerDeleteContactChange] = useDeleteContactChangeMutation()

  const { getContactChange } = useGetContactChange(serviceId, user, updateProps)

  const deleteContactChange = useCallback(
    async (contactChangeId: string) => {
      try {
        await triggerDeleteContactChange({ accountId, contactChangeId })
      } catch (e) {
        console.error(e)
      }
    },
    [accountId, triggerDeleteContactChange],
  )

  const { data: contactSchemaResponse } = useGetContactSchemaQuery(useGetContactSchemaArgs(account?.data ?? null, user, serviceId))

  const rerunContactUpdate = useCallback(async () => {
    if (!contactSchemaResponse) {
      return
    }
    const createdContactExternalId = contact?.integrations?.find((e) => e.serviceId === serviceId)?.externalId ?? 'new'
    const externalInfo = contact?.externalInfos.find((e) => e.serviceId === serviceId)

    if (INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId as IntegrationServiceId) && (!createdContactExternalId || !externalInfo)) {
      return
    }

    dispatch(setIsLoadingRerun(true))
    try {
      if (contactChange) {
        await deleteContactChange(contactChange.id)
      }

      const schema = INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId as IntegrationServiceId)
        ? (externalInfo?.contactSchema ?? {})
        : contactSchemaResponse.data
      const values = externalInfo?.contactValues ?? null
      await getContactChange(createdContactExternalId, schema, values)
    } catch (e) {
      console.log(e)
    } finally {
      dispatch(setIsLoadingRerun(false))
      onRerun?.()
    }
  }, [contact, contactChange, deleteContactChange, dispatch, getContactChange, onRerun, serviceId, contactSchemaResponse])

  return {
    rerunContactUpdate,
  }
}

export function useEMoneyOwnerSyncedFormData(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialData: Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: Record<string, any>,
  serviceId: string,
  rootFieldId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: (name: string, value: any, options?: SetValueConfig) => void,
) {
  const [formData, setFormData] = useState(initialData)

  const predefinedMap = useMemo(() => {
    const map = new Map()
    const oneOfOptions = schema?.properties?.clientPriorities?.items?.oneOf

    if (Array.isArray(oneOfOptions)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      oneOfOptions.forEach((option: any) => {
        const id = option.properties?.id?.const
        const descriptionDefault = option.properties?.description?.default

        if (id && descriptionDefault !== undefined) {
          map.set(id, { description: descriptionDefault })
        }
      })
    }
    return map
  }, [schema])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function updateFormData(newData: Record<string, any>) {
    const updatedFormData = { ...newData }
    if (serviceId !== 'emoney') {
      setFormData(updatedFormData)
      setValue(rootFieldId, updatedFormData, { shouldDirty: true, shouldValidate: true })
      return
    }
    if (predefinedMap.size > 0 && Array.isArray(updatedFormData.clientPriorities)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updatedFormData.clientPriorities = updatedFormData.clientPriorities.map((priority: any, index: number) => {
        const updatedPriority = { ...priority }

        updatedPriority.rank = index + 1

        if (priority && predefinedMap.has(priority.id)) {
          const expected = predefinedMap.get(priority.id)
          if (priority.description !== expected.description) {
            updatedPriority.description = expected.description
          }
        }

        return updatedPriority
      })
    }
    if (predefinedMap.size > 0 && updatedFormData?.spousePriorities && Array.isArray(updatedFormData.spousePriorities)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updatedFormData.spousePriorities = updatedFormData.spousePriorities.map((priority: any, index: number) => {
        const updatedPriority = { ...priority }

        updatedPriority.rank = index + 1

        if (priority && predefinedMap.has(priority.id)) {
          const expected = predefinedMap.get(priority.id)
          if (priority.description !== expected.description) {
            updatedPriority.description = expected.description
          }
        }

        return updatedPriority
      })
    }

    Object.entries(OWNER_PATHS_BY_FIELD).forEach(([fieldKey, typeToFields]) => {
      const fieldArray = updatedFormData[fieldKey]
      if (Array.isArray(fieldArray) && typeToFields) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updatedFormData[fieldKey] = fieldArray.map((item: any) => {
          const updatedItem = { ...item }

          Object.entries(typeToFields).forEach(([fieldToSync, nestedKeys]) => {
            nestedKeys.forEach((nestedKey) => {
              const nestedArray = item[nestedKey]
              if (Array.isArray(nestedArray)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                updatedItem[nestedKey] = nestedArray.map((owner: any) => ({
                  ...owner,
                  [fieldToSync]: item[fieldToSync],
                }))
              }
            })
          })

          return updatedItem
        })
      }
    })

    setFormData(updatedFormData)
    setValue(rootFieldId, updatedFormData, { shouldDirty: true, shouldValidate: true })
  }

  return [formData, updateFormData, setFormData] as const
}

interface PanelInfosProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  panel: { [key: string]: any }
  metadata: object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactSchema: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactValues: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previousContactValues?: { [key: string]: any }
  key: string
}

export const getPanelInfos = ({ panel, metadata, contactSchema, contactValues, key }: PanelInfosProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getParentRequiredSteps = (panel: { [key: string]: any }, submittedValueKeys: string[]): string[] => {
    const panelRequiredSteps = getPanelRequiredSteps(panel, submittedValueKeys)
    return [...new Set(Object.values(panelRequiredSteps).flat())] as string[]
  }

  const getFailedStepsInPanel = (requiredSteps: string[], metadata: object): string[] => {
    const failedOperations = Object.keys(metadata || {})
    return requiredSteps.filter((step: string) => failedOperations.includes(step))
  }

  const getChildStatuses = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    panel: { [key: string]: any },
    metadata: object,
    submittedValueKeys: string[],
  ): ChildStatus[] => {
    const panelRequiredSteps = getPanelRequiredSteps(panel, submittedValueKeys)
    return Object.keys(panelRequiredSteps).map((key) => {
      const childRequiredSteps = panelRequiredSteps[key] ?? []
      const childFailedSteps = getFailedStepsInChild(childRequiredSteps, metadata)
      return getChildStatus(childFailedSteps)
    })
  }

  const getStatusFromChildStatuses = (childStatuses: ChildStatus[]): PanelStatus => {
    if (!childStatuses.length || childStatuses.every((status) => status === 'success')) {
      return 'success'
    } else if (childStatuses.every((status) => status === 'error')) {
      return 'error'
    } else {
      return 'warning'
    }
  }

  const getStatusFromChildren = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    panel: { [key: string]: any },
    metadata: object,
    submittedValueKeys: string[],
  ): PanelStatus => {
    const childStatuses = getChildStatuses(panel, metadata, submittedValueKeys)
    return getStatusFromChildStatuses(childStatuses)
  }

  const getPanelErrorMessages = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    panel: { [key: string]: any },
    metadata: object,
    submittedValueKeys: string[],
  ): string[] => {
    const requiredSteps = getParentRequiredSteps(panel, submittedValueKeys)
    const failedStepsInPanel = getFailedStepsInPanel(requiredSteps, metadata)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return failedStepsInPanel.map((step: any) => metadata[step as keyof typeof metadata])
  }

  const statusVisuals: Record<PanelStatus, { icon: string; color: string }> = {
    success: { icon: 'bi-check-circle-fill', color: 'text-success' },
    error: { icon: 'bi-x-circle-fill', color: 'text-danger' },
    warning: { icon: 'bi-exclamation-triangle-fill', color: 'text-warning' },
  }

  const submittedValueKeys = Object.keys(contactValues[key] ?? {})
  const panelStatus = getStatusFromChildren(panel, metadata, submittedValueKeys)
  const { icon, color } = statusVisuals[panelStatus]
  const errorMessages = getPanelErrorMessages(panel, metadata, submittedValueKeys)

  const isEmptyValue = (value: unknown): boolean => {
    if (typeof value === 'string') {
      return value.trim() === ''
    }
    if (Array.isArray(value)) {
      return value.every(isEmptyValue)
    }
    if (value && typeof value === 'object') {
      return Object.values(value).every(isEmptyValue)
    }
    return false
  }

  const showUpToDateMessage = !contactSchema[key] || !contactValues[key] || isEmptyValue(contactValues[key])

  const shouldDisplayStatusIcon = !showUpToDateMessage && metadata && !!Object.keys(metadata).length && getNumberOfChildren(panel, submittedValueKeys) > 0

  return { icon, color, errorMessages, showUpToDateMessage, shouldDisplayStatusIcon }
}

export const usePanelInfos = (props: PanelInfosProps) => {
  return getPanelInfos(props)
}

interface FieldInfoProps {
  fieldId: string
  requiredSteps: { [key: string]: string[] }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: { [key: string]: any }
  serviceId: IntegrationServiceId
}

export const useGetSubSectionError = ({ fieldId, requiredSteps, metadata, serviceId }: FieldInfoProps) => {
  const subSection = getFieldSubSectionId(fieldId)
  const shouldDisplayStatusIcon = getShouldDisplayStatusIcon(subSection, requiredSteps, metadata)

  const errorMessages = shouldDisplayStatusIcon ? getFieldErrorMessages(requiredSteps, subSection, metadata, serviceId) : []
  return { errorMessages }
}

interface UseFetchContactProps {
  serviceId: string
}

export const useFetchContact = ({ serviceId }: UseFetchContactProps) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetIntegrationUsers] = useLazyGetIntegrationUsersQuery()
  const [contactByEmail, setContactByEmail] = useState<User | null>()
  const fetchContact = useCallback(
    async (externalId: string) => {
      dispatch(setIsLoadingContact(true))
      dispatch(setContact(null))
      try {
        const { data: contacts } = await triggerGetIntegrationUsers({
          accountId,
          connector: serviceId,
          filters: { externalIds: [externalId] },
          includeExternalInfos: true,
        }).unwrap()
        if (contacts.length) {
          dispatch(setContact(contacts[0]))
        }
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setIsLoadingContact(false))
        dispatch(setIsInitializing(false))
      }
    },
    [accountId, dispatch, serviceId, triggerGetIntegrationUsers],
  )

  const searchForEmail = useCallback(
    async (email: string) => {
      if (contactByEmail) {
        dispatch(setContact(contactByEmail))
        return
      }
      dispatch(setIsLoadingContact(true))
      dispatch(setIsSearchingForEmail(true))
      dispatch(setContact(null))
      try {
        const { data: contacts } = await triggerGetIntegrationUsers({
          accountId,
          connector: serviceId,
          filters: { email: email },
          includeExternalInfos: false,
        }).unwrap()
        if (contacts.length) {
          dispatch(setContact(contacts[0]))
          setContactByEmail(contacts[0])
          dispatch(setCreateNewCRMContact(false))
        }
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setIsLoadingContact(false))
        dispatch(setIsSearchingForEmail(false))
        dispatch(setIsInitializing(false))
      }
    },
    [accountId, dispatch, serviceId, triggerGetIntegrationUsers, contactByEmail],
  )

  return { fetchContact, searchForEmail }
}

interface useInitializeContactChangeProps {
  serviceId: string
  user: User
  updateProps: ExternalContactUpdateProps
  onCancel: () => void
}

export const useInitializeContactChange = ({ serviceId, user, updateProps, onCancel }: useInitializeContactChangeProps) => {
  const accountId = useAppSelector(selectAccountId)
  const contact = useAppSelector(selectContact)
  const contactRef = useRef(contact)
  // A ref rather than state: the gate has to close before the first await, not after a render.
  const isInitializingRef = useRef(false)
  const onCancelRef = useRef(onCancel)

  const { trackingClient } = useTracking()

  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const isInitializing = useAppSelector(selectIsInitializing)
  useEffect(() => {
    contactRef.current = contact
  }, [contact])
  useEffect(() => {
    onCancelRef.current = onCancel
  }, [onCancel])

  const { data: contactSchemaResponse } = useGetContactSchemaQuery(useGetContactSchemaArgs(account?.data ?? null, user, serviceId))

  const getErrorMessage = (error: unknown): string => {
    if (
      typeof error === 'object' &&
      error !== null &&
      'data' in error &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      typeof (error as any).data === 'object' &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error as any).data !== null &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      'message' in (error as any).data
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return String((error as any).data.message)
    }

    return 'Something went wrong. If the issue persists, please contact us at support@zocks.io'
  }

  const newContactData = useAppSelector(selectNewContactData)
  const shouldCreateContact = useAppSelector(selectShouldCreateContact)
  const contactChange = useAppSelector(selectContactChange)
  const { getContactChange } = useGetContactChange(serviceId, user, updateProps)
  const dispatch = useAppDispatch()
  const [triggerCreateIntegrationContact] = useCreateIntegrationContactMutation()
  const [triggerPromoteUser] = usePromoteUserByIdMutation()
  const contactUpdateStep = useAppSelector(selectContactUpdateStep)

  const initializeContactChange = useCallback(async () => {
    const currentContact = contactRef.current
    if (!currentContact) {
      const { schema, values, externalId } = await (async () => {
        if (INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId as IntegrationServiceId)) {
          if (!newContactData) {
            return { schema: contactSchemaResponse?.data, externalId: 'new' }
          }
          const integrationDisplayName = getIntegrationDisplayName(serviceId as IntegrationServiceId)
          try {
            const { firstName, lastName, name, email, ownerId } = newContactData
            trackingClient.track({
              eventName: TrackEvent.EXTERNAL_CONTACT_CREATED,
              eventDetails: {
                serviceId,
                ZocksContactType: user?.type,
                source: updateProps.type,
              },
            })
            const createdContact = await triggerCreateIntegrationContact({
              accountId,
              connector: serviceId,
              contact: {},
              firstName,
              lastName,
              name,
              email,
              contactChangeId: contactChange?.id,
              ownerId,
            }).unwrap()

            if (createdContact && createdContact.data) {
              dispatch(
                addToast({
                  variant: 'success',
                  title: 'Contact created',
                  messages: [`Your ${integrationDisplayName} contact was successfully created`],
                }),
              )
              const externalContact = createdContact.data
              if (shouldCreateContact) {
                try {
                  await triggerPromoteUser({
                    accountId,
                    userId: user.id,
                    userData: {
                      firstName: externalContact.firstName,
                      lastName: externalContact.lastName,
                      name: externalContact.name,
                      email: externalContact.email,
                      roleIds: [DEFAULT_CONTACT_ROLE_ID],
                      integrations: externalContact.integrations,
                      identity: {
                        type: 'external',
                        location: externalContact.email,
                      },
                      owner: {
                        ownerId: currentUser.id,
                        ownerType: UserOwnerType.USER,
                      },
                    },
                  }).unwrap()
                  dispatch(
                    addToast({
                      variant: 'success',
                      title: 'Contact created in Zocks',
                      messages: ['Contact created in Zocks'],
                    }),
                  )
                  dispatch(setShouldReload(true))
                } catch (error) {
                  dispatch(
                    addToast({
                      variant: 'error',
                      title: 'Contact save to Zocks failed',
                      messages: [getErrorMessage(error)],
                    }),
                  )
                } finally {
                  dispatch(setShouldCreateContact(false))
                }
              }

              dispatch(setContact(createdContact.data))

              return {
                schema: createdContact.data.externalInfos.find((e) => e.serviceId === serviceId)?.contactSchema,
                values: createdContact.data.externalInfos.find((e) => e.serviceId === serviceId)?.contactValues,
                externalId: createdContact.data.integrations?.find((e) => e.serviceId === serviceId)?.externalId ?? 'new',
              }
            } else {
              dispatch(
                addToast({
                  variant: 'error',
                  title: `Contact create to ${integrationDisplayName} failed`,
                  messages: [
                    typeof createdContact?.errors === 'string'
                      ? createdContact.errors
                      : 'Something went wrong. If the issue persists, please contact us at support@zocks.io',
                  ],
                }),
              )
              onCancelRef.current()
              return { schema: null, values: null, externalId: null }
            }
          } catch (error) {
            dispatch(
              addToast({
                variant: 'error',
                title: `Contact create to ${integrationDisplayName} failed`,
                messages: [getErrorMessage(error)],
              }),
            )
            onCancelRef.current()
            return { schema: null, externalId: null }
          }
        } else {
          return { schema: contactSchemaResponse?.data, externalId: 'new' }
        }
      })()
      if (schema && externalId) {
        if (serviceId === IntegrationServiceId.RIGHT_CAPITAL) {
          await getContactChange(externalId, schema, values ?? null)
          return
        }
        await getContactChange(externalId, schema, null)
      }
      return
    }
    const integration = currentContact.integrations?.find((e) => e.serviceId === serviceId)
    const externalInfo = currentContact.externalInfos.find((e) => e.serviceId === serviceId)
    if (!integration || !externalInfo) {
      return
    }
    if (shouldCreateContact) {
      try {
        await triggerPromoteUser({
          accountId,
          userId: user.id,
          userData: {
            firstName: currentContact.firstName,
            lastName: currentContact.lastName,
            name: currentContact.name,
            email: currentContact.email,
            roleIds: [DEFAULT_CONTACT_ROLE_ID],
            integrations: currentContact.integrations,
            identity: {
              type: 'external',
              location: currentContact.email,
            },
            owner: {
              ownerId: currentUser.id,
              ownerType: UserOwnerType.USER,
            },
          },
        }).unwrap()
        dispatch(
          addToast({
            variant: 'success',
            title: 'Contact created in Zocks',
            messages: ['Contact created in Zocks'],
          }),
        )
        dispatch(setShouldReload(true))
      } catch (error) {
        dispatch(
          addToast({
            variant: 'error',
            title: 'Contact save to Zocks failed',
            messages: [getErrorMessage(error)],
          }),
        )
      } finally {
        dispatch(setShouldCreateContact(false))
      }
    }

    const schema = INTEGRATIONS_WITH_DYNAMIC_SCHEMA.includes(serviceId as IntegrationServiceId) ? externalInfo.contactSchema : contactSchemaResponse?.data

    await getContactChange(integration.externalId, schema ?? {}, externalInfo.contactValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getContactChange,
    serviceId,
    contactSchemaResponse,
    accountId,
    currentUser.id,
    dispatch,
    contactRef,
    newContactData,
    triggerCreateIntegrationContact,
    triggerPromoteUser,
    user.id,
    shouldCreateContact,
    trackingClient,
    user?.type,
    updateProps.type,
  ])

  useEffect(() => {
    if (!contactSchemaResponse || isInitializing || contactUpdateStep !== CONTACT_UPDATE_STEP.CONTACT_UPDATE_STEP) {
      return
    }
    if (isInitializingRef.current) {
      return
    }

    isInitializingRef.current = true
    // Keep the contactRef effect above this one, or a later render creates a second contact.
    initializeContactChange().finally(() => {
      isInitializingRef.current = false
    })
  }, [contactSchemaResponse, isInitializing, initializeContactChange, contactUpdateStep])

  return { initializeContactChange }
}

export const useSectionNavigation = () => {
  const [activeSection, setActiveSection] = useState<NavigationSidebarSection | null>(null)
  const [sections, setSection] = useState<NavigationSidebarSection[]>([])

  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections])

  const currentIndex = activeSection ? sectionIds.indexOf(activeSection.id) : 0

  const canPrevious = currentIndex > 0 && sections.slice(0, currentIndex).some((section) => section.disabled === false)
  const canNext = currentIndex !== -1 && currentIndex < sections.length - 1 && sections.slice(currentIndex + 1).some((section) => section.disabled === false)

  const handleNext = () => {
    if (canNext) {
      const nextEnabledSection = sections.slice(currentIndex + 1).find((section) => section.disabled === false)
      if (nextEnabledSection) {
        setActiveSection(nextEnabledSection)
      }
    }
  }

  const handlePrevious = () => {
    if (canPrevious) {
      const previousEnabledSection = sections
        .slice(0, currentIndex)
        .reverse()
        .find((section) => section.disabled === false)
      if (previousEnabledSection) {
        setActiveSection(previousEnabledSection)
      }
    }
  }

  const handleSectionsLoaded = (ids: NavigationSidebarSection[]) => {
    if (JSON.stringify(ids) !== JSON.stringify(sections)) {
      setSection(ids)
      if (!activeSection && ids.length > 0) {
        setActiveSection(null)
      }
    }
  }

  return {
    activeSection,
    sections,
    setActiveSection,
    handleSectionsLoaded,
    handleNext,
    handlePrevious,
    canNext,
    canPrevious,
  }
}
